// MARK:- OS 2.0 web component for accordion-based sections (product-about, product-tips, etc.)
//
// File naming: standalone web components in this theme use the `component-` prefix
// in the FILE NAME (`assets/component-tabs.js`, `assets/component-accordion.js`, …)
// so they sort together in the assets directory and are easy to grep. The custom
// element name, JS class, and data attributes stay short.
//
// Architecture: hand-edited asset, NOT a webpack/gulp build output. Sections that
// need accordions include it via:
//
//   <script src="{{ 'component-accordion.js' | asset_url }}" type="module"></script>
//
// `type="module"` loads + executes once per page regardless of how many sections
// reference it. Shopify's CDN handles minification + cache headers.
//
// Markup contract (prefixed attributes to avoid colliding with master's legacy
// jQuery `.accordion` driver in src/js/theme.js, which uses unprefixed
// .accordion__toggle / .accordion__content / .is-initially-open / .is-open):
//
//   <as-accordion>
//     <button data-as-accordion-toggle aria-expanded="false">…</button>
//     <div data-as-accordion-content hidden>…</div>
//
//     <button data-as-accordion-toggle data-as-initially-open aria-expanded="true">…</button>
//     <div data-as-accordion-content>…</div>
//   </as-accordion>
//
// Pairing: each toggle is paired with the next sibling content element. Markup
// authors don't have to wire ids; just keep the toggle/content pairs adjacent.
//
// Behavior:
// - On connect: any toggle with [data-as-initially-open] starts open; others start
//   closed. Aria-expanded + the `is-open` class on toggle, content, and the parent
//   item wrapper (closest [data-as-accordion-item], if present) reflect state.
// - Click on a toggle flips its own state. Independent toggles — opening one does
//   not close others (matches master's accordion behavior).
// - Exclusive mode: when the host element carries [data-as-accordion-exclusive],
//   opening one toggle auto-closes every other open toggle in the same
//   <as-accordion>. PDP product-about hardcodes this; the generic accordion block
//   exposes it as a setting (default on). Without the attribute, multiple items
//   can stay open at once (used by product-tips).
// - Self-init via connectedCallback so customizer section reloads work without
//   a `shopify:section:load` listener.

class AsAccordion extends HTMLElement {
  connectedCallback() {
    AsAccordion.ensureBaseStyle();
    this.bindEvents();
    this.initState();
  }

  static ensureBaseStyle() {
    if (document.querySelector('style[data-as-accordion-base]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-as-accordion-base', '');
    style.textContent = 'as-accordion { display: block; }';
    document.head.appendChild(style);
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-as-accordion-toggle]');
      if (!toggle || !this.contains(toggle)) return;
      event.preventDefault();
      this.setOpen(toggle, !this.isOpen(toggle));
    });
  }

  initState() {
    // MARK:- Strip the markup-level [hidden] attribute on connect. From here
    // out the CSS in component-accordion.css drives visibility purely off the
    // is-open class — content sits at max-height: 0 / opacity: 0 when closed
    // so the open/close transition runs smoothly. [hidden] still owns the
    // pre-JS no-flash fallback for closed items in the source markup.
    this.querySelectorAll('[data-as-accordion-content]').forEach((content) => {
      content.removeAttribute('hidden');
    });

    this.querySelectorAll('[data-as-accordion-toggle]').forEach((toggle) => {
      const initiallyOpen = toggle.hasAttribute('data-as-initially-open');
      this.setOpen(toggle, initiallyOpen);
    });
  }

  isOpen(toggle) {
    return toggle.getAttribute('aria-expanded') === 'true';
  }

  setOpen(toggle, open) {
    // MARK:- Exclusive mode — close any other open toggles in this <as-accordion>
    // before opening this one. Guarded on `open` so the recursive close calls
    // below don't re-enter this branch.
    if (open && this.hasAttribute('data-as-accordion-exclusive')) {
      this.querySelectorAll('[data-as-accordion-toggle]').forEach((other) => {
        if (other !== toggle && this.isOpen(other)) this.setOpen(other, false);
      });
    }

    const content = this.contentFor(toggle);
    const item = toggle.closest('[data-as-accordion-item]');
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.classList.toggle('is-open', open);
    if (content) {
      content.classList.toggle('is-open', open);
    }
    if (item) item.classList.toggle('is-open', open);
  }

  contentFor(toggle) {
    let sibling = toggle.nextElementSibling;
    while (sibling) {
      if (sibling.matches('[data-as-accordion-content]')) return sibling;
      sibling = sibling.nextElementSibling;
    }
    return null;
  }
}

if (!customElements.get('as-accordion')) {
  customElements.define('as-accordion', AsAccordion);
}
